import React, { useState, useEffect, useMemo } from 'react';
import Header from '../components/Header';
import FilterBar, { FilterSummary } from '../components/FilterBar';
import DisclaimerFooter from '../components/DisclaimerFooter';
import { chipData } from '../data/chipData';
import './Chip.css';
import { usePersistentFlag, usePersistentSet } from '../utils/expandedState';

const TARGET_YEARS = [
  { value: '2026', label: '2026' },
  { value: '2027', label: '2027' },
  { value: '2028', label: '2028' },
  { value: '2029', label: '2029' },
];

function yearClass(year) {
  return `chip-year-${year}`;
}

function getAllObjectives(data) {
  const objs = [];
  data.priorities.forEach(p => p.objectives.forEach(o => objs.push(o)));
  return objs;
}

function Chip() {
  const [sel, setSel] = useState({ targetYear: new Set() });
  const [expandedPriorities, setExpandedPriorities] = usePersistentSet('chip:expandedPriorities', () => new Set(chipData.priorities.map(p => p.id)));
  const [expandedObjectives, setExpandedObjectives] = usePersistentSet('chip:expandedObjectives');
  const [overviewExpanded, setOverviewExpanded] = usePersistentFlag('chip:overviewExpanded');
  const [keyExpanded, setKeyExpanded] = usePersistentFlag('chip:keyExpanded');

  useEffect(() => {
    document.title = 'Community Health Improvement Plan';
  }, []);

  const allObjectives = useMemo(() => getAllObjectives(chipData), []);

  const dimensions = useMemo(() => [
    { key: 'targetYear', label: 'Target Year', values: TARGET_YEARS.map(y => ({
      value: y.value, label: y.label,
      count: allObjectives.filter(o => o.targetYear === y.value).length,
    })) },
  ], [allObjectives]);

  const toggleValue = (key, value) => setSel(prev => {
    const next = new Set(prev[key]);
    if (next.has(value)) next.delete(value); else next.add(value);
    return { ...prev, [key]: next };
  });
  const clearDimension = (key) => setSel(prev => ({ ...prev, [key]: new Set() }));
  const clearAllFilters = () => setSel({ targetYear: new Set() });

  const matchesObjective = useMemo(() => {
    return (o) => {
      if (sel.targetYear.size && !sel.targetYear.has(o.targetYear)) return false;
      return true;
    };
  }, [sel]);

  const filteredPriorities = useMemo(() => {
    return chipData.priorities.map(p => ({
      ...p,
      objectives: p.objectives.filter(matchesObjective)
    })).filter(p => p.objectives.length > 0);
  }, [matchesObjective]);

  const totalFiltered = useMemo(() => {
    return filteredPriorities.reduce((sum, p) => sum + p.objectives.length, 0);
  }, [filteredPriorities]);

  const togglePriority = (id) => {
    setExpandedPriorities(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleObjective = (id) => {
    setExpandedObjectives(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedPriorities(new Set(chipData.priorities.map(p => p.id)));
    setExpandedObjectives(new Set(allObjectives.map(o => o.id)));
    setOverviewExpanded(true);
    setKeyExpanded(true);
  };

  const collapseAll = () => {
    setExpandedPriorities(new Set());
    setExpandedObjectives(new Set());
    setOverviewExpanded(false);
    setKeyExpanded(false);
  };

  return (
    <div className="chip-page">
      <a href="#main-content" className="chip-skip-link">Skip to main content</a>
      <Header
        title="Community Health Improvement Plan"
        creditExtra="website design by Michael Rubenstein"
      />

      <main id="main-content" tabIndex={-1} className="chip-content">
        <div className="chip-intro">
          <p className="chip-updated">
            Town of Brookline · Department of Public Health &amp; Human Services · {chipData.planYears} CHIP
          </p>
        </div>

        <div className="chip-controls">
          <FilterBar
            allCount={allObjectives.length}
            dimensions={dimensions}
            selected={sel}
            onToggleValue={toggleValue}
            onClearDimension={clearDimension}
            onClearAll={clearAllFilters}
            trailing={
              <>
                <button className="chip-expand-btn" onClick={expandAll}>Expand All</button>
                <button className="chip-expand-btn" onClick={collapseAll}>Collapse All</button>
              </>
            }
          />
        </div>

        <FilterSummary
          count={totalFiltered}
          noun="objective"
          nounPlural="objectives"
          dimensions={dimensions}
          selected={sel}
          onClearAll={clearAllFilters}
        />

        <div className="chip-preamble-sections">
          <div className="chip-preamble">
            <button
              className={`chip-preamble-header ${overviewExpanded ? 'expanded' : ''}`}
              onClick={() => setOverviewExpanded(!overviewExpanded)}
              aria-expanded={overviewExpanded}
            >
              <span className={`chip-chevron${overviewExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="chip-preamble-title">About This Plan</span>
            </button>
            {overviewExpanded && (
              <div className="chip-preamble-body">
                {chipData.introduction.overview.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            )}
          </div>

          <div className="chip-preamble">
            <button
              className={`chip-preamble-header ${keyExpanded ? 'expanded' : ''}`}
              onClick={() => setKeyExpanded(!keyExpanded)}
              aria-expanded={keyExpanded}
            >
              <span className={`chip-chevron${keyExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="chip-preamble-title">About the Objectives &amp; Target-Year Key</span>
            </button>
            {keyExpanded && (
              <div className="chip-preamble-body">
                {chipData.introduction.itemsNote.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
                <ul className="chip-year-key">
                  {chipData.legend.targetYear.map(y => (
                    <li key={y.code}>
                      <span className={`chip-year-badge ${yearClass(y.code)}`}>{y.code}</span>
                      <span className="chip-year-key-label">{y.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="chip-priorities">
          {filteredPriorities.map(priority => (
            <div key={priority.id} className="chip-priority">
              <h2 className="chip-heading"><button
                className={`chip-priority-header ${expandedPriorities.has(priority.id) ? 'expanded' : ''}`}
                onClick={() => togglePriority(priority.id)}
                aria-expanded={expandedPriorities.has(priority.id)}
              >
                <span className={`chip-chevron chip-chevron-light${expandedPriorities.has(priority.id) ? ' expanded' : ''}`} aria-hidden="true"></span>
                <div className="chip-priority-header-text">
                  <span className="chip-priority-name">Priority {priority.number}: {priority.name}</span>
                  {priority.description && (
                    <span className="chip-priority-desc">{priority.description}</span>
                  )}
                </div>
                <span className="chip-priority-count" aria-hidden="true">{priority.objectives.length}</span>
              </button></h2>

              {expandedPriorities.has(priority.id) && (
                <div className="chip-priority-body">
                  <p className="chip-goal">{priority.goalStatement}</p>
                  {priority.objectives.map(o => {
                    const isExpanded = expandedObjectives.has(o.id);
                    return (
                      <div key={o.id} className="chip-objective">
                        <h3 className="chip-heading"><button
                          className={`chip-objective-header ${isExpanded ? 'expanded' : ''}`}
                          onClick={() => toggleObjective(o.id)}
                          aria-expanded={isExpanded}
                        >
                          <span className={`chip-chevron${isExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
                          <span className="chip-objective-id">Objective {o.id}</span>
                          <span className="chip-objective-title">{o.title}</span>
                          <span className={`chip-year-badge small ${yearClass(o.targetYear)}`}>{o.targetYear}</span>
                        </button></h3>
                        {isExpanded && (
                          <div className="chip-objective-body">
                            <p className="chip-strategies-label">Strategies</p>
                            <ul className="chip-strategies-list">
                              {o.strategies.map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="chip-source">
          <p>
            Source: Town of Brookline {chipData.planYears} Community Health Improvement Plan (CHIP), January 2025,
            Department of Public Health and Human Services with Health Resources in Action. This page presents the
            plan’s four priority areas, goals, objectives, and strategies; the community health assessment, key
            partners, outcome indicators, and appendices are in the source document.
          </p>
        </div>

        <DisclaimerFooter>
          <p>
            For source information from the Town of Brookline, go to the{' '}
            <a href="https://www.brooklinema.gov/3632/Community-Health-Assessment-Improvement-" target="_blank" rel="noopener noreferrer">
              Community Health Assessment &amp; Improvement Plan<span className="visually-hidden"> (opens in new tab)</span>
            </a>
            {' '}web page on the Town of Brookline website.
          </p>
        </DisclaimerFooter>
      </main>
    </div>
  );
}

export default Chip;
